import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authAPI } from '../services/api';
import { disconnectSocket } from '../hooks/useSocket';
import { syncAppBadge } from '../services/appBadge';

interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  company?: string | null;
  companyLogo?: string | null;
  avatar?: string | null;
  role: string;
  permissions: string[];
  restrictions?: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
  verifySession: () => Promise<void>;
  refreshProfile: () => Promise<User | null>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const { data } = await authAPI.login(email, password);
          if (data.success) {
            sessionStorage.setItem('accessToken', data.accessToken);
            set({
              user: data.user,
              accessToken: data.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          } else {
            set({ error: data.message, isLoading: false });
            return false;
          }
        } catch (err: unknown) {
          const msg = (err as { response?: { data?: { message?: string } } })
            ?.response?.data?.message || 'Error de conexión';
          set({ error: msg, isLoading: false });
          return false;
        }
      },

      logout: async () => {
        try {
          await authAPI.logout();
        } catch {
          // ignorar error de logout
        } finally {
          disconnectSocket();
          sessionStorage.removeItem('accessToken');
          syncAppBadge(0).catch(() => undefined);
          set({ user: null, accessToken: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),

      verifySession: async () => {
        const token = sessionStorage.getItem('accessToken');
        if (!token) {
          set({ user: null, accessToken: null, isAuthenticated: false });
          return;
        }
        try {
          const { data } = await authAPI.profile();
          set({
            user: data.user,
            accessToken: token,
            isAuthenticated: true,
          });
        } catch {
          sessionStorage.removeItem('accessToken');
          set({ user: null, accessToken: null, isAuthenticated: false });
        }
      },

      refreshProfile: async () => {
        const token = sessionStorage.getItem('accessToken');
        if (!token) {
          set({ user: null, accessToken: null, isAuthenticated: false });
          return null;
        }

        try {
          const { data } = await authAPI.profile();
          set({
            user: data.user,
            accessToken: token,
            isAuthenticated: true,
          });
          return data.user;
        } catch {
          sessionStorage.removeItem('accessToken');
          set({ user: null, accessToken: null, isAuthenticated: false });
          return null;
        }
      },
    }),
    {
      name: 'auth-storage',
      storage: {
        getItem: (key) => { const v = sessionStorage.getItem(key); return v ? JSON.parse(v) : null; },
        setItem: (key, value) => sessionStorage.setItem(key, JSON.stringify(value)),
        removeItem: (key) => sessionStorage.removeItem(key),
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
      }) as any,
    }
  )
);
